import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { useEffect, useState } from "react";
import styled from "styled-components";
import UserTimingDialog from "../PageUtils/Operator/UserChangeTimeDialog";
import UserMessageDialog from "../PageUtils/Operator/UserMessageDialog";
import UserQuestionsDialog from "../PageUtils/Operator/UserQuestionsDialog";
import UserReturnDialog from "../PageUtils/Operator/UserReturnDialog";
import UserSkipDialog from "../PageUtils/Operator/UserSkipDialog";
import UserTimelineDialog from "../PageUtils/Operator/UserTimelineDialog";
import UserPopupDialog from "../PageUtils/Operator/UserPopupDialog";
import UserJumpDialog from "../PageUtils/Operator/UserJumpDialog";
import GlobalOperationsDialog from "../PageUtils/Operator/GlobalOperationsDialog";
import TopNav from "../PageUtils/User/TopNav/TopNav";
import TimelineIcon from "@mui/icons-material/Timeline";
import GroupsIcon from "@mui/icons-material/Groups";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { requestUsers, getTests } from "./OperatorAPI/OperatorGet";
import {
  postAllowToiletBrake,
  postMarkAllRead,
  postSendMessageToEveryone,
  postForcePauseUser,
  postForceResumeUser,
  notifyOperator,
} from "./OperatorAPI/OperatorPost";

const Root = styled.div`
  margin: 2px;
`;

const Title = styled(Typography)`
  margin-top: 24px !important;
  margin-bottom: 24px !important;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 64px;
`;

const ListItemStyled = styled(ListItem)`
  cursor: pointer;
  &:nth-child(even) {
    ${({ raiseHand }) => !raiseHand && `background-color: #f5f5f5;`}
  }
  &:hover {
    background-color: #e0e0e0;
  }
  ${({ needsToiletbrake }) =>
    needsToiletbrake &&
    `
        color: white;
    `}
  ${({ raiseHand }) =>
    raiseHand &&
    `
        background-color: yellow;
    `}
`;


const OperatorPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [acceptAutomaticallyToiletBrake, setAcceptAutomaticallyToiletBrake] =
    useState(true);
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
  const [openSkipDialog, setOpenSkipDialog] = useState(false);
  const [openTimingDialog, setOpenTimingDialog] = useState(false);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [openTimelineDialog, setOpenTimelineDialog] = useState(false);
  const [openPopupDialog, setOpenPopupDialog] = useState(false);
  const [openJumpDialog, setOpenJumpDialog] = useState(false);
  const [openGlobalMessageConfirm, setOpenGlobalMessageConfirm] = useState(false);
  const [openGlobalOperations, setOpenGlobalOperations] = useState(false);
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState(""); // Step 1: Add searchQuery state variable
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    onlineStatus: 'all',
    idleStatus: 'all',
    testId: '',
    chapter: '',
    questionIndex: '',
    hasRaisedHand: false,
    needsToiletBrake: false,
  });
  const [availableTests, setAvailableTests] = useState([]);
  const [sortBy, setSortBy] = useState('lastChatted'); // 'name', 'position', 'lastChatted'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'

  const clearFilters = () => {
    setFilters({
      onlineStatus: 'all',
      idleStatus: 'all',
      testId: '',
      chapter: '',
      questionIndex: '',
      hasRaisedHand: false,
      needsToiletBrake: false,
    });
    setSearchQuery("");
  };

  const updateUsers = users => {
    const usersWithoutNull = users.filter(
      user => user !== null && user.stoppedQuestionnaire != true
    );
    setUsers(usersWithoutNull);
  };

  useEffect(() => {
    if (acceptAutomaticallyToiletBrake) {
      users.forEach(user => {
        if (user.toilet_brake && !user.at_the_toilet) {
          acceptToiletbrake(user);
        }
      });
    }
  }, [users, acceptAutomaticallyToiletBrake]);

  useEffect(() => {
    const getTestNames = async () => {
      try {
        setAvailableTests(await getTests());
      } catch (err) {
        // The filter dropdown degrades to "All Tests"; nothing worth interrupting the operator for.
        console.warn("Failed to load test names:", err?.message);
      }
    };
    getTestNames();

    const intervalId = setInterval(() => {
      requestUsers(updateUsers);
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const handleClose = () => {
    setOpenQuestionDialog(false);
    setOpenSkipDialog(false);
    setOpenTimingDialog(false);
    setOpenReturnDialog(false);
    setOpenMessageDialog(false);
    setOpenTimelineDialog(false);
    setOpenPopupDialog(false);
    setOpenJumpDialog(false);
    setOpenGlobalMessageConfirm(false);
    setSelectedUser(null);
  };

  const formatTime = time => {
    if (!Number.isFinite(time)) return "--:--";
    if (time <= 0) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const formatIdleStatus = user => {
    if (user.idleMs === null || user.idleMs === undefined) return { text: "Never active", color: "text.secondary" };

    // Formula: server_idle + time_since_received + half_rtt
    const timeSinceReceived = Date.now() - user.receivedAt;
    const currentIdleMs = user.idleMs + timeSinceReceived + (user.rtt / 2);

    const diffSeconds = Math.floor(currentIdleMs / 1000);
    if (diffSeconds < 60) return { text: "Active just now", color: "success.main" };
    const mins = Math.floor(diffSeconds / 60);

    const color = mins >= 5 ? "warning.main" : "text.secondary";
    return { text: `Idle for ${mins}m`, color };
  };

  const getIdleMinutes = user => {
    if (user.idleMs === null || user.idleMs === undefined) return Infinity;
    const timeSinceReceived = Date.now() - user.receivedAt;
    const currentIdleMs = user.idleMs + timeSinceReceived + (user.rtt / 2);
    return Math.floor(currentIdleMs / (1000 * 60));
  };

  const COMPARATORS = {
    lastChatted: (a, b) => (a.raise_hand === b.raise_hand ? (b.last_chatted || 0) - (a.last_chatted || 0) : a.raise_hand ? -1 : 1),
    name: (a, b) => a.name.localeCompare(b.name),
    position: (a, b) => {
      if (a.test_index !== b.test_index) return a.test_index - b.test_index;
      if (a.chapter_index !== b.chapter_index) return a.chapter_index - b.chapter_index;
      return a.questionnaire_question_index_in_chapter - b.questionnaire_question_index_in_chapter;
    },
  };

  const handleSearch = event => {
    setSearchQuery(event.target.value); // Step 3: Update searchQuery state variable
  };

  const acceptToiletbrake = user => {
    try {
      postAllowToiletBrake(user.id);
    } catch (e) {
      console.log(e);
    }
  };

  const sendMessage = () => {
    // Send message to server
    postSendMessageToEveryone(message);
    setMessage("");
  };

  /**
   * Number of raise-hand issues still waiting on the operator — the same thing that
   * turns the row yellow.
   */
  const unreadIssuesCount = user =>
    (user.raise_hand_questions || []).filter(q => q.isOpen && !q.isAllAnswered).length;

  /** Clear the yellow highlight for a user without answering their issues. */
  const markAsRead = async user => {
    const result = await postMarkAllRead(user.id);
    if (result.ok) {
      notifyOperator(`הפניות של ${user.name} סומנו כנקראו`, "success");
    }
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesOnline = filters.onlineStatus === 'all' ||
      (filters.onlineStatus === 'online' && user.isOnline) ||
      (filters.onlineStatus === 'offline' && !user.isOnline);

    const idleMins = getIdleMinutes(user);
    const matchesIdle = filters.idleStatus === 'all' ||
      (filters.idleStatus === 'idle5' && idleMins >= 5) ||
      (filters.idleStatus === 'active' && idleMins < 5);

    const matchesTest = filters.testId === '' || String(user.questionnaire_test).includes(filters.testId);
    const matchesChapter = filters.chapter === '' || String(user.questionnaire_chapter).toLowerCase().includes(String(filters.chapter).toLowerCase());
    const matchesQuestion = filters.questionIndex === '' || user.questionnaire_question_index_in_chapter === filters.questionIndex;

    const matchesRaisedHand = !filters.hasRaisedHand || user.raise_hand;
    const matchesToilet = !filters.needsToiletBrake || (user.toilet_brake && !user.at_the_toilet);

    return matchesSearch && matchesOnline && matchesIdle && matchesTest && matchesChapter && matchesQuestion && matchesRaisedHand && matchesToilet;
  });

  const onlineUsersCount = users.reduce((count, user) => count + (user.isOnline ? 1 : 0), 0);
  const allProcessedUsers = filteredUsers.sort((a, b) => {
    const comparator = COMPARATORS[sortBy] || COMPARATORS.lastChatted;
    const res = comparator(a, b);
    return sortOrder === 'asc' ? res : -res;
  });

  const usersPerPage = 20;
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const paginatedUsers = allProcessedUsers.slice(indexOfFirstUser, indexOfLastUser);

  return (
    <>
      <TopNav />
      <Root>
        <Title variant="h4">Operator Page</Title>
        <Box sx={{ p: 2, mb: 3, bgcolor: "grey.50", borderRadius: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Global Operations */}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<GroupsIcon />}
              onClick={() => setOpenGlobalOperations(true)}
              sx={{ fontWeight: "bold", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              פעולות גלובליות
            </Button>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

            {/* Auto Toilet Toggle */}
            <Box sx={{ minWidth: 200 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={acceptAutomaticallyToiletBrake}
                    onChange={event => setAcceptAutomaticallyToiletBrake(event.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Accept Toilet Breaks Automatically
                  </Typography>
                }
              />
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

            {/* Global Message */}
            <Box sx={{ flexGrow: 1, minWidth: 300 }}>
              <TextField
                size="small"
                fullWidth
                dir="rtl"
                placeholder="הודעה לכל הנבחנים..."
                value={message}
                onChange={event => setMessage(event.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        color="primary"
                        onClick={() => setOpenGlobalMessageConfirm(true)}
                        disabled={!message.trim()}
                      >
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

            {/* Search and Filter Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: "100%", md: 350 } }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search candidates..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Tooltip title="Toggle Filters">
                <IconButton
                  color={showFilters ? "primary" : "default"}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ bgcolor: showFilters ? 'rgba(25, 118, 210, 0.08)' : 'transparent' }}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        </Box>

        {showFilters && (
          <Box sx={{ p: 2, mb: 3, bgcolor: "white", border: '1px solid', borderColor: 'grey.200', borderRadius: 1, mx: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
              {/* Online Status */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Online Status</InputLabel>
                <Select
                  value={filters.onlineStatus}
                  label="Online Status"
                  onChange={e => setFilters({ ...filters, onlineStatus: e.target.value })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                </Select>
              </FormControl>

              {/* Idle Status */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Activity</InputLabel>
                <Select
                  value={filters.idleStatus}
                  label="Activity"
                  onChange={e => setFilters({ ...filters, idleStatus: e.target.value })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active (&lt; 5m)</MenuItem>
                  <MenuItem value="idle5">Idle (&gt; 5m)</MenuItem>
                </Select>
              </FormControl>

              {/* Test ID (now Select) */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Test</InputLabel>
                <Select
                  value={filters.testId}
                  label="Test"
                  onChange={e => setFilters({ ...filters, testId: e.target.value })}
                >
                  <MenuItem value="">All Tests</MenuItem>
                  {availableTests.map(testName => (
                    <MenuItem key={testName} value={testName}>{testName}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Chapter */}
              <TextField
                size="small"
                label="Chapter"
                value={filters.chapter}
                onChange={e => setFilters({ ...filters, chapter: e.target.value })}
                sx={{ width: 120 }}
              />

              {/* Question */}
              <TextField
                size="small"
                label="Question"
                type="number"
                value={filters.questionIndex}
                onChange={e => setFilters({ ...filters, questionIndex: Number(e.target.value) })}
                sx={{ width: 90 }}
              />

              {/* Toggles (Checkbox) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  sx={{ mr: 0, '& .MuiFormControlLabel-label': { ml: -1 } }}
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.hasRaisedHand}
                      onChange={e => setFilters({ ...filters, hasRaisedHand: e.target.checked })}
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Raised Hand</Typography>}
                />

                <FormControlLabel
                  sx={{ mr: 0, '& .MuiFormControlLabel-label': { ml: -1 } }}
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.needsToiletBrake}
                      onChange={e => setFilters({ ...filters, needsToiletBrake: e.target.checked })}
                      sx={{ p: 0.5 }}
                    />
                  }
                  label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Requested Toilet</Typography>}
                />
              </Box>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{ ml: 'auto' }}
              >
                Clear
              </Button>
            </Stack>
          </Box >
        )}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "grey.100", // Light gray background
          p: 1.5,
          borderRadius: 0,
          mb: 1.5,
          flexWrap: "wrap",
          gap: 2
        }}>
          {/* Pagination Controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              sx={{ minWidth: 100 }}
            >
              Previous
            </Button>

            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 100, textAlign: "center", color: "text.secondary" }}>
              Page {currentPage} of {Math.ceil(allProcessedUsers.length / usersPerPage) || 1}
            </Typography>

            <Button
              variant="contained"
              size="small"
              onClick={handleNextPage}
              disabled={indexOfLastUser >= allProcessedUsers.length}
              sx={{ minWidth: 100 }}
            >
              Next
            </Button>
          </Box>

          {/* User Metrics */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Sort By (Always Visible) */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={e => setSortBy(e.target.value)}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="lastChatted">Last Chatted</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="position">Position</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}>
              <IconButton
                size="small"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'grey.300' }}
              >
                {sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: 'rgba(0,0,0,0.05)', px: 1.5, py: 0.75, borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>Total Users</Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: 'text.primary', minWidth: 20, textAlign: 'center' }}>
                {users.length}
              </Typography>
            </Box>

            {allProcessedUsers.length !== users.length && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: 'rgba(25, 118, 210, 0.1)', px: 1.5, py: 0.75, borderRadius: 1.5 }}>
                <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>Shown</Typography>
                <Typography variant="body2" sx={{ fontWeight: 900, color: 'primary.main', minWidth: 20, textAlign: 'center' }}>
                  {allProcessedUsers.length}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, bgcolor: 'rgba(46, 125, 50, 0.1)', px: 1.5, py: 0.75, borderRadius: 1.5 }}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: "success.main" }} />
              <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>Online</Typography>
              <Typography variant="body2" sx={{ fontWeight: 900, color: 'success.main', minWidth: 20, textAlign: 'center' }}>
                {onlineUsersCount}
              </Typography>
            </Box>
          </Box>
        </Box>

        <List>
          {paginatedUsers.map(user => (
            <ListItemStyled
              key={user.id}
              needsToiletbrake={
                user.toilet_brake &&
                !user.at_the_toilet &&
                !acceptAutomaticallyToiletBrake
              }
              raiseHand={user.raise_hand}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: user.isOnline ? "success.main" : "text.disabled",
                          boxShadow: user.isOnline ? "0 0 8px #4caf50" : "none"
                        }}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 1 }}>
                        {user.isOnline ? "Online" : "Offline"}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle1" component="span" fontWeight="800" sx={{ color: 'text.primary' }}>
                      {user.name}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.8, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    {/* Position Data - LTR Box */}
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: 'rgba(0,0,0,0.04)',
                      border: '1px solid',
                      borderColor: 'rgba(0,0,0,0.1)',
                      borderRadius: 1,
                      px: 1,
                      height: 24, // Unified height
                      boxSizing: 'border-box',
                      dir: 'ltr' // Explicit LTR for position info
                    }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold', lineHeight: 1 }}>{user.questionnaire}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>/</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>{user.questionnaire_test}</Typography>
                      {user.questionnaire_state !== "TEST_END" && (
                        <>
                          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>/</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>chapter {user.chapter_index + 1}</Typography>
                        </>
                      )}
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>/</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
                        {(() => {
                          const state = user.questionnaire_state;
                          if (state === "REFERENCES") return "references";
                          if (state === "CHAPTER_END") return "chapter end";
                          if (state === "TEST_END") return "test end";
                          if (!state || state === "QUESTION") {
                            return user.questionnaire_question_index_in_chapter >= 0
                              ? `question ${user.questionnaire_question_index_in_chapter + 1}`
                              : 'question N/A';
                          }
                          return state?.toLowerCase() || "N/A";
                        })()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", bgcolor: 'rgba(0,0,0,0.04)', px: 1.5, height: 24, borderRadius: 1, boxSizing: 'border-box' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1 }}>References:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: Number.isFinite(user.reference_time_left) ? 'primary.main' : 'text.disabled', lineHeight: 1 }}>
                        {formatTime(user.reference_time_left)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", bgcolor: 'rgba(0,0,0,0.04)', px: 1.5, height: 24, borderRadius: 1, boxSizing: 'border-box' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1 }}>Chapter:</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: Number.isFinite(user.chapter_time_left) ? 'primary.main' : 'text.disabled', lineHeight: 1 }}>
                        {formatTime(user.chapter_time_left)}
                      </Typography>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 16, alignSelf: 'center' }} />

                    {/* Personal Data */}
                    <Chip
                      label={`Gender: ${user.gender || "-"}`}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 1, height: 24, fontSize: '0.7rem', color: 'text.secondary', borderColor: 'rgba(0,0,0,0.1)', boxSizing: 'border-box' }}
                    />

                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      height: 24,
                      bgcolor: formatIdleStatus(user).color === 'warning.main' ? 'rgba(237, 108, 2, 0.08)' : 'transparent',
                      px: 1,
                      borderRadius: 1,
                      boxSizing: 'border-box'
                    }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: formatIdleStatus(user).color,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          lineHeight: 1
                        }}
                      >
                        {formatIdleStatus(user).text}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              {user.toilet_brake &&
                !user.at_the_toilet &&
                !acceptAutomaticallyToiletBrake && (
                  <Button
                    onClick={() => {
                      acceptToiletbrake(user);
                    }}
                    color="primary"
                  >
                    Allow Toilet brake
                  </Button>
                )}
              {
                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenTimingDialog(true);
                    setOpenSkipDialog(false);
                    setOpenReturnDialog(false);
                    setOpenQuestionDialog(false);
                    setOpenMessageDialog(false);
                    setOpenPopupDialog(false);
                    setOpenJumpDialog(false);
                  }}
                >
                  Timing User
                </Button>
              }
              {unreadIssuesCount(user) > 0 && (
                <Button
                  color="success"
                  variant="contained"
                  size="small"
                  startIcon={<DoneAllIcon />}
                  onClick={() => markAsRead(user)}
                  sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                >
                  סמן כנקרא ({unreadIssuesCount(user)})
                </Button>
              )}
              {
                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenQuestionDialog(true);
                    setOpenTimingDialog(false);
                    setOpenSkipDialog(false);
                    setOpenReturnDialog(false);
                    setOpenMessageDialog(false);
                    setOpenPopupDialog(false);
                    setOpenJumpDialog(false);
                  }}
                >
                  Answer Questions
                </Button>
              }
              {
                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenSkipDialog(true);
                    setOpenQuestionDialog(false);
                    setOpenReturnDialog(false);
                    setOpenTimingDialog(false);
                    setOpenMessageDialog(false);
                    setOpenPopupDialog(false);
                    setOpenJumpDialog(false);
                  }}
                >
                  Skip User
                </Button>
              }
              {
                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenReturnDialog(true);
                    setOpenTimingDialog(false);
                    setOpenSkipDialog(false);
                    setOpenQuestionDialog(false);
                    setOpenMessageDialog(false);
                    setOpenPopupDialog(false);
                    setOpenJumpDialog(false);
                  }}
                >
                  Return User
                </Button>
              }
              {
                <Button
                  color="secondary"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenJumpDialog(true);
                    setOpenReturnDialog(false);
                    setOpenTimingDialog(false);
                    setOpenSkipDialog(false);
                    setOpenQuestionDialog(false);
                    setOpenMessageDialog(false);
                    setOpenPopupDialog(false);
                  }}
                  sx={{ fontWeight: "bold" }}
                >
                  קפיצה לפרק ⏭
                </Button>
              }
              {user.force_paused ? (
                <Button
                  color="success"
                  variant="contained"
                  onClick={() => postForceResumeUser(user.id)}
                >
                  המשך מבחן ▶
                </Button>
              ) : (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => postForcePauseUser(user.id)}
                >
                  עצור מבחן ⏸
                </Button>
              )}
              {
                <Button
                  color="primary"
                  onClick={() => {
                    setSelectedUser(user);
                    console.log("open message dialog for user:", user);
                    setOpenMessageDialog(true);
                    setOpenReturnDialog(false);
                    setOpenTimingDialog(false);
                    setOpenSkipDialog(false);
                    setOpenQuestionDialog(false);
                    setOpenPopupDialog(false);
                    setOpenJumpDialog(false);
                  }}
                >
                  Send Message
                </Button>
              }
              {
                <Button
                  color="warning"
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenPopupDialog(true);
                    setOpenMessageDialog(false);
                    setOpenReturnDialog(false);
                    setOpenTimingDialog(false);
                    setOpenSkipDialog(false);
                    setOpenQuestionDialog(false);
                    setOpenJumpDialog(false);
                  }}
                >
                  Send Popup
                </Button>
              }
              {
                <Button
                  color="secondary"
                  variant="outlined"
                  size="small"
                  startIcon={<TimelineIcon />}
                  onClick={() => {
                    setSelectedUser(user);
                    setOpenTimelineDialog(true);
                    setOpenMessageDialog(false);
                    setOpenReturnDialog(false);
                    setOpenTimingDialog(false);
                    setOpenSkipDialog(false);
                    setOpenQuestionDialog(false);
                    setOpenPopupDialog(false);
                    setOpenJumpDialog(false);
                  }}
                  sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: 'fit-content' }}
                >
                  Timeline
                </Button>
              }
              {selectedUser && (
                <>
                  {openQuestionDialog && (
                    <UserQuestionsDialog
                      onClose={handleClose}
                      user={selectedUser}
                    />
                  )}
                  {openReturnDialog && (
                    <UserReturnDialog
                      onClose={handleClose}
                      user={selectedUser}
                    ></UserReturnDialog>
                  )}
                  {openSkipDialog && (
                    <UserSkipDialog
                      onClose={handleClose}
                      user={selectedUser}
                    ></UserSkipDialog>
                  )}
                  {openTimingDialog && (
                    <UserTimingDialog
                      onClose={handleClose}
                      user={selectedUser}
                    ></UserTimingDialog>
                  )}
                  {openMessageDialog && (
                    <UserMessageDialog
                      onClose={handleClose}
                      user={selectedUser}
                    ></UserMessageDialog>
                  )}
                  {openPopupDialog && (
                    <UserPopupDialog
                      onClose={handleClose}
                      user={selectedUser}
                    />
                  )}
                  {openJumpDialog && (
                    <UserJumpDialog
                      onClose={handleClose}
                      user={selectedUser}
                    />
                  )}
                  {openTimelineDialog && (
                    <UserTimelineDialog
                      onClose={handleClose}
                      user={selectedUser}
                    />
                  )}
                </>
              )}

              {user.at_the_toilet && (
                <span
                  role="img"
                  aria-label="toilet-emoji"
                  style={{ fontSize: "2em" }}
                >
                  🚽
                </span>
              )}
            </ListItemStyled>
          ))}
        </List>

        {openGlobalOperations && (
          <GlobalOperationsDialog
            onClose={() => setOpenGlobalOperations(false)}
            allUsers={users}
            filteredUsers={allProcessedUsers}
          />
        )}

        {/* Global Message Confirmation Dialog */}
        <Dialog
          open={openGlobalMessageConfirm}
          onClose={() => setOpenGlobalMessageConfirm(false)}
          PaperProps={{ dir: 'rtl' }}
        >
          <DialogTitle>שלח הודעה לכולם</DialogTitle>
          <DialogContent>
            <DialogContentText>
              האם אתה בטוח שברצונך לשלוח את ההודעה הבאה לכל המשתמשים?
            </DialogContentText>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <Typography dir="rtl" variant="body1">
                {message}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenGlobalMessageConfirm(false)}>ביטול</Button>
            <Button
              onClick={() => {
                sendMessage();
                setOpenGlobalMessageConfirm(false);
              }}
              variant="contained"
              color="primary"
            >
              שלח לכולם
            </Button>
          </DialogActions>
        </Dialog>
      </Root >
    </>
  );
};

export default OperatorPage;
